import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { useUserContext } from "./useUserContext";
import { updateJobV2 } from "../_firebase/jobs";
import { JobV2 } from "../@types/job";
import useSendStageUpdateEmail from "./useSendStageUpdateEmail";

import { Client, ClientJobV2 } from "../@types/client";
import { isSpecimenLineItem } from "../_helpers/lineItem";
import { JobSpecimenLineItem } from "@/app/@types/job";
import useSendStageUpdateSMS from "./SMS/useSendStageUpdateSMS";
import { useSubscriptionContext } from "./subscription/useSubscriptionContext";
import { getStages } from "../_firebase/stages";
import { patchJobsIndexEntry } from "../_firebase/jobsIndex";
import { useSetAlert } from "./useAlertContext";

interface MutationVariables {
  updateJobFormData: JobV2;
  job: JobV2;
  // Split payments: money already moved, so QuickBooks failures are collected
  // into qbFailures instead of aborting the save.
  qbBestEffort?: boolean;
}

type MutationData = {
  updatedJob: JobV2;
  updatedClientJob: ClientJobV2;
  partMoveFailures?: string[];
  qbFailures?: string[];
};
type ErrorType = Error;

export const useUpdateJob = (): UseMutationResult<
  MutationData,
  ErrorType,
  MutationVariables
> => {
  const queryClient = useQueryClient();
  const user = useUserContext();
  const subscription = useSubscriptionContext();

  const sendStateUpdateEmail = useSendStageUpdateEmail();
  const sendStateUpdateSMS = useSendStageUpdateSMS();
  const setAlert = useSetAlert();

  const mutation = useMutation<MutationData, ErrorType, MutationVariables>({
    mutationFn: ({ updateJobFormData, job, qbBestEffort }: MutationVariables) =>
      // Stages come from the React Query cache when present (already fetched
      // by the pages that edit jobs), so the save skips a metadata read.
      updateJobV2(user, job, updateJobFormData, {
        stages: queryClient.getQueryData<StageType[]>([user.uid, "stages"]),
        qbBestEffort,
      }),
    onSuccess: async (data: MutationData, variables: MutationVariables) => {
      if (data.partMoveFailures?.length) {
        setAlert({
          type: "warning",
          title: "Job saved, but part locations need attention",
          content: `${data.partMoveFailures.join(
            " "
          )} Try the move again from the job or part page.`,
          persistent: true,
        });
      }

      const stages =
        queryClient.getQueryData<StageType[]>([user.uid, "stages"]) ??
        (await getStages(user.uid));
      const stageUpdates = data.updatedJob.lineItems
        .filter((lineItem) => isSpecimenLineItem(lineItem))
        .filter((lineItem) => {
          const stageToUpdateTo = stages.find(
            (stage) => stage.name === lineItem.stage
          );
          return stageToUpdateTo?.autoStageUpdates === true;
        })
        .reduce((updates, newLineItem) => {
          const oldLineItem = variables.job.lineItems.find(
            (item) => isSpecimenLineItem(item) && item.uid === newLineItem.uid
          ) as JobSpecimenLineItem | undefined;

          if (!oldLineItem || oldLineItem.stage !== newLineItem.stage) {
            updates.push({
              specimen: newLineItem.specimen.species!,
              stage: newLineItem.stage!,
            });
          }
          return updates;
        }, [] as Array<{ specimen: string; stage: string }>);

      let sendEmail;
      let sendSMS;

      if (data.updatedJob.client.autoStageEmails === true) {
        sendEmail = true;
      } else if (data.updatedJob.client.autoStageEmails === false) {
        sendEmail = false;
      } else if (data.updatedJob.client.autoStageEmails === null) {
        sendEmail = user.settings.autoStageEmails;
      }

      if (stageUpdates.length === 0) {
        return;
      }

      const phone = data.updatedJob.client.contactDetails.phone;
      if (
        subscription.smsCount >= subscription.smsLimit ||
        !phone ||
        !phone.startsWith("+1")
      ) {
        sendSMS = false;
      } else if (data.updatedJob.client.autoStageSMS === true) {
        sendSMS = true;
      } else if (data.updatedJob.client.autoStageSMS === false) {
        sendSMS = false;
      } else if (data.updatedJob.client.autoStageSMS === null) {
        sendSMS = user.settings.autoStageSMS;
      }

      if (sendEmail && data.updatedJob.client.contactDetails.email) {
        stageUpdates.forEach((update) => {
          sendStateUpdateEmail({
            jobSpecimen: update.specimen,
            stage: update.stage,
            clientUid: data.updatedJob.client.uid,
            clientEmail: data.updatedJob.client.contactDetails.email,
            jobUid: data.updatedJob.uid,
            jobId: data.updatedJob.jobId,
          });
        });
      }

      if (sendSMS && stageUpdates.length > 0) {
        const updates = stageUpdates.map((update) => ({
          stage: update.stage,
          specimen: update.specimen,
        }));

        sendStateUpdateSMS({
          clientUid: data.updatedJob.client.uid,
          clientPhone: data.updatedJob.client.contactDetails.phone,
          jobUid: data.updatedJob.uid,
          jobId: data.updatedJob.jobId,
          updates,
        });
      }

      queryClient.setQueryData<JobV2 | undefined>(
        ["jobs", data.updatedJob.jobId],
        (oldData) => {
          return data.updatedJob;
        }
      );

      // Index mode: reflect the edit in the list immediately; the
      // syncJobsIndex trigger's write confirms it 1-10s later.
      patchJobsIndexEntry(queryClient, user.uid, data.updatedJob);

      // Client switch: the job left one client's list and joined another's,
      // so the in-place patch below would be wrong for both. Refetch them.
      if (variables.job.client.uid !== data.updatedJob.client.uid) {
        queryClient.invalidateQueries({
          queryKey: ["clients", variables.job.client.uid],
        });
        queryClient.invalidateQueries({
          queryKey: ["clients", data.updatedJob.client.uid],
        });
      } else {
        queryClient.setQueryData<Client | undefined>(
          ["clients", data.updatedJob.client.uid],
          (oldData) => {
            if (oldData) {
              const updatedClient = {
                ...oldData,
                jobs: oldData.jobs.map((clientJob) =>
                  clientJob.uid === data.updatedJob.uid
                    ? data.updatedClientJob
                    : clientJob
                ),
              };
              return updatedClient;
            }
            return oldData;
          }
        );
      }

      queryClient.invalidateQueries({ queryKey: ["dashboardStats"] });

      // A payment (or any job change) can alter the invoice, so never serve a
      // stale cached invoice afterwards. Bust both invoice PDF caches:
      //  - QuickBooks PDF: [user.uid, "qb", "invoicePdf", qbInvoiceId]
      //  - MountMonitor PDF: ["invoicePdf", jobUid]
      if (data.updatedJob.qbInvoiceId) {
        queryClient.invalidateQueries({
          queryKey: [
            user.uid,
            "qb",
            "invoicePdf",
            data.updatedJob.qbInvoiceId,
          ],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["invoicePdf", data.updatedJob.uid],
      });
    },
  });

  return mutation;
};
